// planCheckout.js
// Shared Stripe plan-subscription checkout (same /products + /create-checkout-session
// flow the web RecommendedPlan uses). Used by the global offer modal and the
// go-live screens so the logic lives in one place.
import axios from "axios";
import { API_BASE_URL } from "../Store/apiStore";
import decodeToken from "./decodeToken";
import { trackEvent, stashPendingPurchase } from "./analytics";
import { markGoLiveCheckoutRedirect } from "../Component/GoLivePayg/GoLiveVariant";
import { isEmbeddedCheckoutEnabled } from "./embeddedCheckoutFlag";
import { useCheckoutStore } from "../Store/checkoutStore";

// Load the Stripe products (plans + prices).
export const fetchPlanProducts = async () => {
  try {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API_BASE_URL}/products`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("[planCheckout] products load failed:", err?.message || err);
    return [];
  }
};

// Check out a plan by NAME (Starter/Scaler/Growth) → Stripe subscription.
// Returns false on any failure (caller can show an error / reset loading).
//
// forNewAgent: pay BEFORE the agent exists (user adding another agent from
// BusinessStep). No agentId, no upgrade; the backend stamps a marker so the
// webhook leaves the user's existing agent alone and createAgent claims the
// plan. Payment then returns to `next` (with `returnParams`) instead of
// /go-live-setup, so the caller can create the agent.
export const checkoutPlan = async ({
  planName,
  yearly = false,
  products = [],
  forNewAgent = false,
  next = "/go-live-setup",
  returnParams = {},
}) => {
  try {
    const token = localStorage.getItem("token") || "";
    const decoded = decodeToken(token);
    if (!decoded || !token) {
      console.error("[planCheckout] not authenticated");
      return false;
    }
    const customerId = decoded.customerId;
    const userId = decoded.id;
    const agentId = forNewAgent ? "" : sessionStorage.getItem("SelectAgentId") || "";

    const wantName = (planName || "").trim().toLowerCase();
    const product = products.find(
      (p) => (p.name || "").trim().toLowerCase() === wantName
    );
    const interval = yearly ? "year" : "month";
    const priceObj = product?.prices?.find((p) => p.interval === interval);
    if (!priceObj?.id) {
      console.error("[planCheckout] no priceId for", wantName, interval);
      return false;
    }

    const successParams = new URLSearchParams({ fromCheckout: "1" });
    if (agentId) successParams.append("agentId", agentId);
    if (userId) successParams.append("userId", userId);
    Object.entries(returnParams).forEach(([k, v]) => successParams.append(k, String(v)));
    /*
     * Thank-you / Setup-Progress interstitial (/payment-success) SIRF $5 PAYG
     * purchase ke liye hai — dekho GoLivePayg ka quick-setup-payg flow.
     *
     * Pehle yahan WebView par /payment-success se guzarte the, isliye MOBILE
     * par Scaler / Starter / Growth khareedne par bhi wo thank-you page aa
     * jata tha, jabki web par seedha destination khulta tha. Ab dono surfaces
     * ek jaise hain.
     */
    const successUrl = `${window.location.origin}${next}?${successParams.toString()}`;
    const cancelUrl = `${window.location.origin}/cancel-payment`;

    // Embedded opens the checkout INSIDE the page (CheckoutSheet), so the live
    // sales call survives it. Hosted is the pre-existing full-page redirect,
    // still used on surfaces embedded isn't enabled for yet.
    const embedded = isEmbeddedCheckoutEnabled();

    const res = await axios.post(
      `${API_BASE_URL}${embedded ? "/create-checkout-session-embedded" : "/create-checkout-session"}`,
      {
        customerId,
        priceId: priceObj.id,
        userId,
        // Embedded mode forbids cancel_url and never redirects, but the backend
        // still needs these to mint the HOSTED fallback session it returns
        // alongside the client secret.
        url: successUrl,
        cancelUrl,
        currency: (priceObj.currency || "usd").toLowerCase(),
        agentId,
        // Deliberate plan change intent (set by FastAgentDetails "Upgrade Plan")
        // — exempts this from the backend's active-subscription idempotency.
        clientFiresPurchase: true,
        // Every existing caller sent isUpgrade: true (a second, earlier key
        // was always overridden by it). A plan for a not-yet-created agent
        // replaces nothing, so it must not be one.
        isUpgrade: !forNewAgent,
        ...(forNewAgent ? { forNewAgent: true } : {}),
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const value =
      priceObj.unit_amount != null ? priceObj.unit_amount / 100 : undefined;
    const currency = (priceObj.currency || "usd").toUpperCase();
    const billing_cycle = interval === "year" ? "yearly" : "monthly";

    // Analytics is identical on both paths — the purchase is the same purchase.
    const trackStart = () => {
      trackEvent("begin_checkout", {
        plan: product.name,
        billing_cycle,
        value,
        currency,
      });
      stashPendingPurchase({
        transaction_id: `${userId || "uid"}-${Date.now()}`,
        plan: product.name,
        billing_cycle,
        value,
        currency,
      });
    };

    if (embedded && res?.data?.clientSecret) {
      trackStart();
      // No awaitAgentIdle() first: the sheet opens instantly and the agent keeps
      // talking over it, so there is nothing to wait for. No endGlobalCall() and
      // no markGoLiveCheckoutRedirect() either — nothing remounts, so that flag
      // would sit unconsumed and eat the NEXT genuine visit's trial/payg pick.
      useCheckoutStore.getState().openCheckout({
        clientSecret: res.data.clientSecret,
        sessionId: res.data.sessionId,
        hostedUrl: res.data.checkoutUrl || null,
        meta: {
          agentId,
          userId,
          planName: product.name,
          value,
          currency,
          // Where the old success_url pointed.
          next,
          extraParams: returnParams,
        },
      });
      return true;
    }

    if (res?.data?.checkoutUrl) {
      trackStart();
      markGoLiveCheckoutRedirect();
      // No pushState before replace(): the pushState pushed a "/" entry that
      // replace() then overwrote, leaving the plan page in history — so Back
      // after payment could return to it and trigger a duplicate charge. Plain
      // replace() overwrites the plan page itself (PRG pattern). A deliberate
      // upgrade is a fresh forward nav, so it stays unaffected.
      window.location.replace(res.data.checkoutUrl);
      return true;
    }
    return false;
  } catch (error) {
    console.error(
      "[planCheckout] checkout error:",
      error?.response?.data || error?.message
    );
    return false;
  }
};
